import {
  IConstraintItem,
  IConstraintOption,
  IConstraintTemplate,
  IConstraintTemplateDetails,
  ICreateConstraintsTemplateRequestBody,
  ICreateConstraintsTemplateResponseBody,
  IUpdateConstraintsTemplateRequestBody,
  IUpdateConstraintsTemplateResponseBody,
} from '@/interfaces/portfolioOptimization.interface'
import axiosInstance from '@/utils/interceptor'

/**
 * Get Constraint Options / Items
 * @returns Promise<IConstraintOption[]> list of constraint options
 */
export const getConstraintOptions = async (): Promise<IConstraintOption[]> => {
  const response = await axiosInstance.get(`/constraints`)
  return response.data as IConstraintOption[]
}

/**
 * Get Constraints Item Details
 * @param id - id of the constraint
 * @param optimizerType - optimizer type
 * @returns Promise<IConstraintItem> constraint item details
 */
export const getConstraintsItemDetails = async (
  id: number,
  optimizerType: string,
): Promise<IConstraintItem> => {
  const response = await axiosInstance.get(
    `constraints/itemId/${id}/optimizerType/${optimizerType}`,
  )
  return response.data as IConstraintItem
}

/* === Constraint Template === */

/**
 * Get Constraint Templates
 * @returns Promise<IConstraintTemplate[]> list of constraint templates
 */
export const getConstraintTemplates = async (): Promise<
  IConstraintTemplate[]
> => {
  const response = await axiosInstance.get(`constraints/constraint-templates`)
  return response.data as IConstraintTemplate[]
}

/**
 * Get Constraint Template Details
 * @param id - id of the constraint template
 * @returns Promise<IConstraintTemplateDetails> constraint template details
 */
export const getConstraintTemplateDetails = async (
  id: string,
): Promise<IConstraintTemplateDetails> => {
  const response = await axiosInstance.get(
    `constraints/constraint-templates/${id}`,
  )
  return response.data as IConstraintTemplateDetails
}

/**
 * Create Constraint Template
 * @param requestBody - request body containing description and constraints
 * @returns Promise<IConstraintTemplate> created constraint template
 */
export const createConstraintTemplate = async (
  requestBody: ICreateConstraintsTemplateRequestBody,
): Promise<ICreateConstraintsTemplateResponseBody> => {
  const response = await axiosInstance.post(
    `constraints/constraint-templates`,
    requestBody,
  )
  return response.data as ICreateConstraintsTemplateResponseBody
}

/**
 * Update Constraint Template
 * @param id - id of the constraint template
 * @param requestBody - request body containing description and constraints
 * @returns Promise<IConstraintTemplate> updated constraint template
 */
export const updateConstraintTemplate = async (
  id: string,
  requestBody: IUpdateConstraintsTemplateRequestBody,
): Promise<IUpdateConstraintsTemplateResponseBody> => {
  const response = await axiosInstance.put(
    `constraints/constraint-templates/${id}`,
    requestBody,
  )
  return response.data as IUpdateConstraintsTemplateResponseBody
}

/**
 * Delete Constraint Template
 * @param id - id of the constraint template
 * @returns Promise<{ success: boolean }>
 */
export const deleteConstraintTemplate = async (
  id: string,
): Promise<{ success: boolean }> => {
  const response = await axiosInstance.delete(
    `constraints/constraint-templates/${id}`,
  )
  return response.data as { success: boolean }
}
